import React, { memo } from 'react'
import cookie from 'js-cookie'
import { useCallback, useLifecycle, useCookies, useHideSolvvy } from 'sb-hooks'
import { useConnect } from 'sb-redux-hook'
import { constants, links, localStorage } from 'common/helpers'
import actions from 'redux/actions'
import { useResubscribeFlows } from 'compositions/resubscribe/util'

/**
 * Auth decorator redirects user if he/she is already logged in,
 * but it does it only on the first render
 *
 * @param ComposedComponent
 * @returns {React.NamedExoticComponent<object> | React.MemoExoticComponent<WrappedComponent>}
 */
const authDecorator = (ComposedComponent) => {

  const WrappedComponent = (props) => {
    useHideSolvvy()

    const { isLoggedIn, isSubscribed, isCancelled, gender } = useConnect({
      isLoggedIn: 'models.me.isLoggedIn',
      isSubscribed: 'models.subscription.isSubscribed',
      isCancelled: 'models.subscription.isCancelled',
      gender: 'models.me.gender',
    })

    const { isSecondTimeResubscribeFlow } = useResubscribeFlows()
    const { getCookie, removeCookie } = useCookies()

    const resubscribeCode = getCookie(constants.cookieNames.resubscribeCode)
    const acquisitionQuizData = getCookie(constants.cookieNames.acquisitionQuizData)

    const redirect = useCallback((values) => {
      // TODO why do we look for "isSubscribed" from values?
      const hasSubscribedStatus = values.isSubscribed || isSubscribed
      const hasCancelledStatus  = values.isCancelled || isCancelled
      const userGender          = values.gender || gender
      const linkToMainPage      = userGender === 'female' ? links.subscription.mainWomen : links.subscription.mainMen
      const link                = values.redirectLink || linkToMainPage

      const isSecondTimeResubscribeFlowMerged = values.isSecondTimeResubscribeFlow || isSecondTimeResubscribeFlow

      localStorage.removeSessionItem(constants.localStorageNames.authLogoRedirectUrl)

      if (acquisitionQuizData) {
        actions.models.router.push(acquisitionQuizData.redirectLink || links.pick)
      }
      else if (hasSubscribedStatus) {
        actions.models.router.push(link)
        removeCookie(constants.cookieNames.logoRedirectUrl)
        removeCookie(constants.cookieNames.resubscribeCode)
      }
      else if (isSecondTimeResubscribeFlowMerged) {
        // SB-1914 redirect cancelled 2 and more times users to landing
        actions.models.router.push(links.resubscribeNoOffer)
        removeCookie(constants.cookieNames.logoRedirectUrl)
        removeCookie(constants.cookieNames.resubscribeCode)
      }
      else if (hasCancelledStatus && resubscribeCode) {
        actions.models.subscription.resubscribe({
          resubscribeCode,
        })
          .then(() => {
            actions.models.router.push(link)
            cookie.remove(constants.cookieNames.logoRedirectUrl)
          }, ({ isRedirected }) => {
            if (!isRedirected) {
              actions.models.router.push(link)
            }
          })
      }
      else {
        // TODO move this to Payment page preloader
        actions.models.cart.addSubscription()
          .then(() => actions.models.router.push(links.subscribe))
      }
    }, [ isSubscribed, isCancelled, isSecondTimeResubscribeFlow, gender, acquisitionQuizData, resubscribeCode, removeCookie ])

    // Redirect on first render
    const isFirstRender = useLifecycle((isFirstRender) => {
      if (isFirstRender) {
        if (isLoggedIn) {
          redirect({})
        }
        else {
          actions.models.google.initialize()
          actions.models.facebook.init()
        }
      }
    }, [ isLoggedIn, redirect ])

    if (isFirstRender && isLoggedIn) {
      return null
    }

    return (
      <ComposedComponent
        {...props}
        onRedirect={redirect}
      />
    )
  }

  WrappedComponent.displayName = `auth(${ComposedComponent.displayName || ComposedComponent.name})`

  return memo(WrappedComponent)
}


export default authDecorator
